import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Sessao } from '../../auth';
import { ErroDeArea, EstadoVazio } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import type { NomeDoIcone } from '../../design/Icone';
import {
  aplicarFolgas,
  diaLongo,
  diaMes,
  diaDaSemana,
  diasDoPeriodo,
  gravarCelulas,
  hojeBrasilia,
  incluirFeriado,
  inicioDaSemana,
  lerCalendarioDeFerias,
  lerEu,
  lerPedidos,
  lerPessoas,
  lerQuadro,
  mesCurto,
  nomeDoDia,
  removerFeriado,
  siglaDoDia,
  somarDias,
} from '../../escala';
import type {
  CelulaDaEscala,
  FeriasDaPessoaNaEscala,
  ControleDaPessoa,
  MudancaDeCelula,
  PedidoDaEscala,
  PessoaDaEscala,
  QuadroDeHorario,
  TurnoDaEscala,
} from '../../escala';
import { AbaCaixas } from './EscalaCaixas';
import { AbaConfigurar, AbaHistorico } from './EscalaConfigurar';
import { AbaControle } from './EscalaControle';
import { AbaMetas } from './EscalaMetas';
import { AbaFerias, MinhasFerias } from './EscalaFerias';
import { AbaPedidos } from './EscalaPedidos';
import { FichaDaPessoa } from './FichaDaPessoa';
import './escala.css';

/**
 * QUADRO DE HORÁRIO (dono, 24/10/2026) — a planilha da loja dentro do InovaPay.
 *
 *   Quadro     a planilha: uma linha por pessoa, uma coluna por dia (3 semanas, segunda a
 *              domingo), domingos e feriados destacados, as cores de sempre. "Minha escala" = só a
 *              minha. Shift + clique seleciona várias células; clicar na data marca feriado.
 *   Caixas     o revezamento dos caixas: quem fica em qual caixa, horário, almoço, folga e férias.
 *   Pedidos    folga, troca, férias e atestado — a equipe pede, Carlos/Nicolas decidem.
 *   Controle   faltas, atestados, férias, feriados trabalhados × compensados, domingos.
 *   Configurar horários dos turnos, feriados e as pessoas do quadro (ordem, entrar, sair).
 *   Histórico  quem mudou o quê.
 *
 * Só quem tem `escala.editar` (Carlos e Nicolas) mexe — o servidor recusa o resto.
 */

type Aba =
  | 'quadro'
  | 'minha'
  | 'caixas'
  | 'pedidos'
  | 'ferias'
  | 'controle'
  | 'metas'
  | 'configurar'
  | 'historico';

const ABAS: readonly { id: Aba; rotulo: string; icone: NomeDoIcone; soEditor?: boolean }[] = [
  { id: 'quadro', rotulo: 'Quadro da loja', icone: 'cal' },
  { id: 'minha', rotulo: 'Minha escala', icone: 'user' },
  { id: 'caixas', rotulo: 'Caixas', icone: 'receipt' },
  { id: 'pedidos', rotulo: 'Pedidos', icone: 'file' },
  { id: 'ferias', rotulo: 'Férias', icone: 'clock', soEditor: true },
  { id: 'controle', rotulo: 'Controle', icone: 'gauge', soEditor: true },
  { id: 'metas', rotulo: 'Metas', icone: 'gauge', soEditor: true },
  { id: 'configurar', rotulo: 'Configurar', icone: 'cog', soEditor: true },
  { id: 'historico', rotulo: 'Histórico', icone: 'clock', soEditor: true },
];

/**
 * Esc fecha a janela. O ouvinte é registrado UMA vez e chama sempre o `aoFechar` mais novo: com
 * `[aoFechar]` no efeito, qualquer redesenho no meio da tecla trocava o ouvinte e o Esc se perdia.
 */
export function useEscFecha(aoFechar: () => void) {
  const ref = useRef(aoFechar);
  ref.current = aoFechar;
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') ref.current();
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, []);
}

export function irParaEscala(rota: string, p: Record<string, string | null>) {
  const atual = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
  for (const [k, v] of Object.entries(p)) {
    if (v === null) atual.delete(k);
    else atual.set(k, v);
  }
  const s = atual.toString();
  window.location.hash = `#/${rota}${s ? `?${s}` : ''}`;
}

export function QuadroDeHorarioTela({
  sessao,
  parametros,
  rota = 'quadro-de-horario',
}: {
  sessao: Sessao;
  parametros: URLSearchParams;
  /** O balconista abre em #/escala; o admin em #/quadro-de-horario. */
  rota?: string;
}) {
  const [podeEditar, setPodeEditar] = useState(false);
  const [pendentes, setPendentes] = useState(0);
  const pedida = parametros.get('aba') as Aba | null;
  const aba: Aba = ABAS.some((a) => a.id === pedida) ? (pedida as Aba) : 'quadro';

  const contarPendentes = useCallback(() => {
    void lerPedidos('PENDENTE').then((r) => r.ok && setPendentes(r.dados.pedidos.length));
  }, []);
  useEffect(contarPendentes, [contarPendentes, aba]);
  // as abas de quem edita aparecem já na abertura, em qualquer aba
  useEffect(() => {
    void lerEu().then((r) => r.ok && setPodeEditar(r.dados.podeEditar));
  }, []);

  return (
    <section className="qh">
      <header className="qh-cabeca">
        <span className="qh-selo" aria-hidden="true">
          <Icone nome="cal" tamanho={22} traco={1.6} />
        </span>
        <div>
          <h1>Quadro de horário</h1>
          <p className="qh-sub">
            A escala da loja: turnos, folgas, domingos e feriados, férias, faltas e atestados.
          </p>
        </div>
      </header>
      <nav className="qh-abas" aria-label="Quadro de horário">
        {ABAS.filter((a) => !a.soEditor || podeEditar).map((a) => (
          <a
            key={a.id}
            href={`#/${rota}${a.id === 'quadro' ? '' : `?aba=${a.id}`}`}
            className={a.id === aba ? 'qh-aba qh-aba-ativa' : 'qh-aba'}
            aria-current={a.id === aba ? 'page' : undefined}
          >
            <Icone nome={a.icone} tamanho={15} />
            {a.rotulo}
            {a.id === 'pedidos' && pendentes > 0 && (
              <span className="qh-badge" aria-label={`${String(pendentes)} aguardando`}>
                {pendentes}
              </span>
            )}
          </a>
        ))}
      </nav>
      {(aba === 'quadro' || aba === 'minha') && (
        <AbaQuadro
          sessao={sessao}
          parametros={parametros}
          rota={rota}
          minha={aba === 'minha'}
          aoSaberPermissao={setPodeEditar}
        />
      )}
      {aba === 'caixas' && <AbaCaixas sessao={sessao} parametros={parametros} rota={rota} />}
      {aba === 'pedidos' && (
        <AbaPedidos sessao={sessao} aoMudar={contarPendentes} aoSaberPermissao={setPodeEditar} />
      )}
      {aba === 'ferias' && <AbaFerias sessao={sessao} />}
      {aba === 'controle' && <AbaControle parametros={parametros} rota={rota} />}
      {aba === 'metas' && podeEditar && <AbaMetas />}
      {aba === 'configurar' && <AbaConfigurar sessao={sessao} />}
      {aba === 'historico' && <AbaHistorico />}
    </section>
  );
}

/* ================================================================== *
 * A célula
 * ================================================================== */

export function estiloDoTurno(t: TurnoDaEscala | undefined): React.CSSProperties {
  if (!t) return {};
  return { color: t.corTexto, background: t.corFundo ?? undefined };
}

/** O que aparece dentro da célula: a marca (X, FE, FA) ou F + o dia do feriado na FC ("F12"). */
export function textoDaCelula(c: CelulaDaEscala, t: TurnoDaEscala | undefined): string {
  if (!t) return c.turno;
  // "F12" = folga referente ao feriado do dia 12 (dono 24/10)
  if (t.categoria === 'COMPENSADA')
    return c.compensa ? `F${String(Number(c.compensa.slice(8, 10)))}` : 'FC';
  return t.marca;
}

export function horarioDoTurno(t: TurnoDaEscala): string | null {
  if (!t.entrada && !t.saida) return null;
  const almoco = t.almocoInicio && t.almocoFim ? ` · almoço ${t.almocoInicio}–${t.almocoFim}` : '';
  return `${t.entrada ?? '?'}–${t.saida ?? '?'}${almoco}`;
}

/* ================================================================== *
 * QUADRO
 * ================================================================== */

const SEMANAS = 3;

function AbaQuadro({
  sessao,
  parametros,
  rota,
  minha,
  aoSaberPermissao,
}: {
  sessao: Sessao;
  parametros: URLSearchParams;
  rota: string;
  minha: boolean;
  aoSaberPermissao: (pode: boolean) => void;
}) {
  // sempre a segunda que abre a semana — um link velho (?de= um sábado) cai na semana certa
  const de = inicioDaSemana(parametros.get('de') ?? hojeBrasilia());
  const ate = somarDias(de, SEMANAS * 7 - 1);
  const [q, setQ] = useState<QuadroDeHorario | null>(null);
  const [erro, setErro] = useState(false);
  const [aviso, setAviso] = useState<{ tom: 'ok' | 'erro'; texto: string } | null>(null);
  const [pincel, setPincel] = useState<string | null>(null);
  const [aberta, setAberta] = useState<{ pessoa: PessoaDaEscala; dia: string }[] | null>(null);
  // Shift + clique: seleção em bloco (dono 26/10); Ctrl + clique liga/desliga uma célula
  const [selecao, setSelecao] = useState<ReadonlySet<string>>(new Set());
  const ancora = useRef<{ pessoa: string; dia: string } | null>(null);
  // copiar e colar semana (segunda a domingo): a semana copiada fica guardada até colar
  const [copiada, setCopiada] = useState<{ semana: string; celulas: CelulaDaEscala[] } | null>(
    null,
  );
  const [feriadoAberto, setFeriadoAberto] = useState<string | null>(null);
  const [ferias, setFerias] = useState<{ pessoaId: string; dia: string }[]>([]);
  const rolagemRef = useRef<HTMLDivElement>(null);
  const [gravando, setGravando] = useState(false);
  // a ficha do funcionário abre ao tocar no nome (ideia do Carlos, 24/10)
  const [ficha, setFicha] = useState<PessoaDaEscala | null>(null);
  const [contas, setContas] = useState<{ accountId: string; nome: string; login: string }[]>([]);
  const abrirFicha = (p: PessoaDaEscala) => {
    setFicha(p);
    void lerPessoas().then((r) => r.ok && setContas(r.dados.contasFora));
  };
  // passar o mouse: acende o cabeçalho do dia (a linha acende pelo CSS) — sem re-render
  const tabelaRef = useRef<HTMLTableElement>(null);
  const acenderDia = (dia: string | null) => {
    const t = tabelaRef.current;
    if (!t) return;
    for (const el of t.querySelectorAll('thead .qh-acesa')) el.classList.remove('qh-acesa');
    if (dia)
      for (const el of t.querySelectorAll(`thead [data-dia="${dia}"]`))
        el.classList.add('qh-acesa');
  };

  const carregar = useCallback(() => {
    setErro(false);
    void lerQuadro(de, ate).then((r) => {
      if (!r.ok) return setErro(true);
      setQ(r.dados);
      aoSaberPermissao(r.dados.podeEditar);
      // férias do mês e as próximas (o rodapé e a folha impressa) — só quem edita lê
      if (r.dados.podeEditar)
        void lerCalendarioDeFerias(`${de.slice(0, 8)}01`, somarDias(de, 200)).then(
          (f) => f.ok && setFerias(f.dados.ferias),
        );
    });
  }, [de, ate, aoSaberPermissao]);
  useEffect(() => {
    setQ(null);
    carregar();
  }, [carregar]);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // só redesenha se havia seleção (redesenhar no meio do Esc trocava o ouvinte da janela aberta)
      setSelecao((atual) => (atual.size > 0 ? new Set() : atual));
      ancora.current = null;
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, []);

  const turnos = useMemo(() => new Map((q?.turnos ?? []).map((t) => [t.codigo, t])), [q]);
  const celulas = useMemo(
    () => new Map((q?.celulas ?? []).map((c) => [`${c.pessoaId}|${c.dia}`, c])),
    [q],
  );
  const feriados = useMemo(() => new Map((q?.feriados ?? []).map((f) => [f.dia, f.nome])), [q]);
  const dias = useMemo(() => diasDoPeriodo(de, ate), [de, ate]);

  const gravar = useCallback(
    async (mudancas: readonly MudancaDeCelula[], texto: string) => {
      if (mudancas.length === 0) return;
      setGravando(true);
      // otimista: a célula muda na hora; se o servidor recusar, recarrega o que vale
      setQ((atual) => (atual ? aplicarLocal(atual, mudancas) : atual));
      const r = await gravarCelulas(sessao, mudancas);
      setGravando(false);
      if (r.tipo === 'confirmada')
        setAviso({ tom: 'ok', texto: r.dados.alteradas ? texto : 'Nada mudou.' });
      else {
        setAviso({
          tom: 'erro',
          texto:
            r.tipo === 'recusada'
              ? r.mensagem
              : 'A internet caiu no meio — confira o quadro (recarreguei).',
        });
        carregar();
      }
    },
    [sessao, carregar],
  );

  if (erro) return <ErroDeArea oQue="o quadro de horário" aoTentar={carregar} />;
  if (!q) return <div className="qh-carregando">Carregando o quadro…</div>;

  const editor = q.podeEditar;
  const eu = q.pessoas.find((p) => p.id === q.euPessoaId) ?? null;
  const linhas = minha ? (eu ? [eu] : []) : q.pessoas;
  const trabalhoOrdenado = q.turnos.filter((t) => t.ativo);

  const chave = (pessoa: string, dia: string) => `${pessoa}|${dia}`;
  const clicar = (
    pessoa: PessoaDaEscala,
    dia: string,
    tecla: { shift: boolean; ctrl: boolean } = { shift: false, ctrl: false },
  ) => {
    if (!editor) return;
    if (tecla.shift) {
      // Shift: do primeiro clique até este, o retângulo inteiro (pessoas × dias)
      const a = ancora.current;
      if (!a) {
        ancora.current = { pessoa: pessoa.id, dia };
        return setSelecao(new Set([chave(pessoa.id, dia)]));
      }
      const ids = linhas.map((x) => x.id);
      const i1 = ids.indexOf(a.pessoa);
      const i2 = ids.indexOf(pessoa.id);
      const [d1, d2] = a.dia <= dia ? [a.dia, dia] : [dia, a.dia];
      const novo = new Set<string>();
      for (const id of ids.slice(Math.min(i1, i2), Math.max(i1, i2) + 1))
        for (const d of diasDoPeriodo(d1, d2)) novo.add(chave(id, d));
      return setSelecao(novo);
    }
    if (tecla.ctrl) {
      ancora.current = { pessoa: pessoa.id, dia };
      const novo = new Set(selecao);
      const k = chave(pessoa.id, dia);
      if (novo.has(k)) novo.delete(k);
      else novo.add(k);
      return setSelecao(novo);
    }
    if (selecao.size > 0) setSelecao(new Set());
    ancora.current = { pessoa: pessoa.id, dia };
    if (pincel === null) return setAberta([{ pessoa, dia }]);
    const atual = celulas.get(`${pessoa.id}|${dia}`);
    const turno = pincel === '-' ? null : pincel;
    if ((atual?.turno ?? null) === turno) return;
    void gravar(
      [{ pessoaId: pessoa.id, dia, turno, ...(turno === 'FC' ? { compensa: null } : {}) }],
      `${pessoa.nome}, ${diaMes(dia)}: ${turno ? (turnos.get(turno)?.rotulo ?? turno) : 'apagado'}.`,
    );
  };

  const abrirSelecao = () => {
    const porId = new Map(linhas.map((p) => [p.id, p]));
    const alvos = [...selecao].flatMap((k) => {
      const [id, dia] = k.split('|');
      const pessoa = id ? porId.get(id) : undefined;
      return pessoa && dia ? [{ pessoa, dia }] : [];
    });
    if (alvos.length > 0) setAberta(alvos);
  };

  /** Copiar: guarda a semana (segunda a domingo) como está na tela. */
  const copiarSemana = (semana: string) => {
    const fim = somarDias(semana, 6);
    const daSemana = q.celulas.filter((c) => c.dia >= semana && c.dia <= fim);
    if (daSemana.length === 0)
      return setAviso({ tom: 'erro', texto: `A semana de ${diaMes(semana)} está vazia.` });
    setCopiada({ semana, celulas: daSemana });
    setAviso({
      tom: 'ok',
      texto: `Semana de ${diaMes(semana)} a ${diaMes(fim)} copiada — agora toque em “Colar aqui” na semana que quer preencher (pode avançar para outras semanas).`,
    });
  };
  /** Colar: dia a dia (segunda → segunda); férias, falta, atestado e FC não vão nem são apagados. */
  const colarSemana = (semana: string) => {
    if (!copiada) return;
    const desloc = Math.round(
      (Date.parse(`${semana}T12:00:00Z`) - Date.parse(`${copiada.semana}T12:00:00Z`)) / 86_400_000,
    );
    const mud: MudancaDeCelula[] = [];
    for (const c of copiada.celulas) {
      const cat = turnos.get(c.turno)?.categoria;
      if (cat !== 'TRABALHO' && cat !== 'FOLGA') continue;
      const dia = somarDias(c.dia, desloc);
      const ja = celulas.get(`${c.pessoaId}|${dia}`);
      const catJa = ja ? turnos.get(ja.turno)?.categoria : null;
      if (catJa && catJa !== 'TRABALHO' && catJa !== 'FOLGA') continue;
      if (ja?.turno === c.turno && (ja.setor ?? null) === (c.setor ?? null)) continue;
      mud.push({ pessoaId: c.pessoaId, dia, turno: c.turno, setor: c.setor });
    }
    if (mud.length === 0)
      return setAviso({ tom: 'ok', texto: `A semana de ${diaMes(semana)} já está igual.` });
    if (
      !window.confirm(
        `Colar a semana de ${diaMes(copiada.semana)} em ${diaMes(semana)} a ${diaMes(somarDias(semana, 6))}? (${String(mud.length)} células; férias, faltas, atestados e folgas de feriado ficam como estão)`,
      )
    )
      return;
    void gravar(mud, `Semana de ${diaMes(copiada.semana)} colada em ${diaMes(semana)}.`);
  };

  /** Rolar a planilha por dias (a barra do rodapé). */
  const rolar = (n: number) => {
    const el = rolagemRef.current;
    if (!el) return;
    const cel = el.querySelector<HTMLElement>('tbody td');
    el.scrollBy({ left: n * (cel?.offsetWidth ?? 34), behavior: 'smooth' });
  };
  const irParaSemana = (semana: string) => {
    const el = rolagemRef.current;
    const th = el?.querySelector<HTMLElement>(`thead tr:nth-child(3) [data-dia="${semana}"]`);
    const nome = el?.querySelector<HTMLElement>('thead .qh-nome');
    if (el && th)
      el.scrollTo({ left: th.offsetLeft - (nome?.offsetWidth ?? 0), behavior: 'smooth' });
  };

  const aplicarFixas = async () => {
    const comFixa = q.pessoas.filter((p) => p.ativo && p.folgaFixa).length;
    if (comFixa === 0)
      return setAviso({
        tom: 'erro',
        texto: 'Ninguém tem folga fixa ainda. Ponha em Configurar → Pessoas do quadro.',
      });
    const substituir = window.confirm(
      `Aplicar as folgas fixas de ${String(comFixa)} pessoas de ${diaMes(de)} a ${diaMes(ate)}.\n\n` +
        'OK = refazer também os dias já marcados com turno/folga comum.\n' +
        'Cancelar = só preencher as células vazias.\n\n' +
        'Férias, faltas, atestados e folgas de feriado nunca são mexidos.',
    );
    setGravando(true);
    const r = await aplicarFolgas(sessao, { de, ate, substituir });
    setGravando(false);
    if (r.tipo === 'confirmada') {
      setAviso({
        tom: 'ok',
        texto: r.dados.alteradas
          ? `Folgas fixas aplicadas: ${String(r.dados.alteradas)} células de ${String(r.dados.pessoas)} pessoas.`
          : 'Nada mudou — o quadro já estava com as folgas fixas.',
      });
      carregar();
    } else
      setAviso({
        tom: 'erro',
        texto: r.tipo === 'recusada' ? r.mensagem : 'A internet caiu no meio — confira o quadro.',
      });
  };

  const semanas = Array.from({ length: SEMANAS }, (_, i) => somarDias(de, i * 7));

  return (
    <div className="qh-corpo">
      <div className="qh-barra qh-nao-imprime">
        <div className="qh-periodo">
          <button
            type="button"
            className="qh-btn-icone"
            onClick={() => irParaEscala(rota, { de: somarDias(de, -7) })}
            aria-label="Semana anterior"
          >
            <Icone nome="arrow-l" tamanho={16} />
          </button>
          <span className="qh-periodo-txt">
            {diaMes(de)} a {diaMes(ate)}
          </span>
          <button
            type="button"
            className="qh-btn-icone"
            onClick={() => irParaEscala(rota, { de: somarDias(de, 7) })}
            aria-label="Próxima semana"
          >
            <Icone nome="arrow" tamanho={16} />
          </button>
          {de !== inicioDaSemana(q.hoje) && (
            <button
              type="button"
              className="qh-btn qh-btn-sec qh-btn-curto"
              onClick={() => irParaEscala(rota, { de: null })}
            >
              Hoje
            </button>
          )}
        </div>
        <div className="qh-acoes">
          {gravando && <span className="qh-gravando">Salvando…</span>}
          {editor && !minha && (
            <button
              type="button"
              className="qh-btn qh-btn-sec"
              onClick={() => void aplicarFixas()}
              title="Preenche as 3 semanas com a folga fixa e o turno padrão de cada pessoa"
            >
              <Icone nome="cal" tamanho={15} />
              Aplicar folgas fixas
            </button>
          )}
          <button type="button" className="qh-btn qh-btn-sec" onClick={() => window.print()}>
            <Icone nome="down" tamanho={15} />
            Imprimir / PDF
          </button>
        </div>
      </div>

      {aviso && (
        <div
          className={`qh-aviso qh-aviso-${aviso.tom} qh-nao-imprime`}
          role={aviso.tom === 'erro' ? 'alert' : 'status'}
        >
          {aviso.texto}
          <button type="button" onClick={() => setAviso(null)} aria-label="Fechar aviso">
            <Icone nome="x" tamanho={14} />
          </button>
        </div>
      )}

      {editor && !minha && (
        <div className="qh-pinceis qh-nao-imprime" role="toolbar" aria-label="Pincel">
          <span className="qh-pinceis-txt">
            {pincel === null
              ? 'Toque numa célula para editar, ou escolha um pincel para pintar várias:'
              : 'Pincel ligado — toque nas células para pintar.'}
          </span>
          {trabalhoOrdenado.map((t) => (
            <button
              key={t.codigo}
              type="button"
              className={`qh-pincel${pincel === t.codigo ? ' qh-pincel-on' : ''}`}
              onClick={() => setPincel(pincel === t.codigo ? null : t.codigo)}
              aria-pressed={pincel === t.codigo}
              title={t.rotulo}
            >
              <span className="qh-amostra" style={estiloDoTurno(t)}>
                {t.categoria === 'COMPENSADA' ? 'FC' : t.marca || ' '}
              </span>
              {t.rotulo}
            </button>
          ))}
          <button
            type="button"
            className={`qh-pincel${pincel === '-' ? ' qh-pincel-on' : ''}`}
            onClick={() => setPincel(pincel === '-' ? null : '-')}
            aria-pressed={pincel === '-'}
          >
            <span className="qh-amostra qh-amostra-borracha">
              <Icone nome="x" tamanho={12} />
            </span>
            Apagar
          </button>
          {pincel !== null && (
            <button type="button" className="qh-btn qh-btn-curto" onClick={() => setPincel(null)}>
              Desligar pincel
            </button>
          )}
        </div>
      )}

      {editor && !minha && (
        <p className="qh-dica-selecao qh-nao-imprime">
          Dica: segure <kbd>Shift</kbd> e toque em duas células para selecionar o bloco entre elas
          (ou <kbd>Ctrl</kbd> para escolher uma a uma); toque na data para marcar feriado.
        </p>
      )}

      {selecao.size > 0 && (
        <div className="qh-selecao qh-nao-imprime" role="status">
          <strong>
            {selecao.size === 1
              ? '1 célula selecionada'
              : `${String(selecao.size)} células selecionadas`}
          </strong>
          <button type="button" className="qh-btn qh-btn-pri qh-btn-curto" onClick={abrirSelecao}>
            <Icone nome="edit" tamanho={14} />
            Alterar todas
          </button>
          <button
            type="button"
            className="qh-btn qh-btn-sec qh-btn-curto"
            onClick={() => {
              setSelecao(new Set());
              ancora.current = null;
            }}
          >
            Limpar seleção
          </button>
        </div>
      )}

      {minha && !eu && (
        <EstadoVazio
          icone="user"
          titulo="Você ainda não está no quadro"
          texto="Peça ao Carlos para colocar você no quadro de horário."
        />
      )}

      {minha && eu && (
        <MinhaEscala q={q} eu={eu} turnos={turnos} celulas={celulas} feriados={feriados} />
      )}

      {!minha && (
        <div className="qh-imprimivel">
          <div className="qh-titulo-impressao">
            <strong>QUADRO DE HORÁRIO</strong>
            <span>
              {diaMes(de)} a {diaMes(ate)}
              {q.atualizadoEm &&
                ` · atualizado ${new Date(q.atualizadoEm).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short', timeStyle: 'short' })}`}
            </span>
          </div>
          <div className="qh-rolagem qh-rolagem-quadro" ref={rolagemRef}>
            <table
              className="qh-tabela"
              ref={tabelaRef}
              onMouseOver={(e) =>
                acenderDia((e.target as HTMLElement).closest('td')?.dataset['dia'] ?? null)
              }
              onMouseLeave={() => acenderDia(null)}
            >
              <thead>
                <tr className="qh-linha-mes">
                  <th className="qh-nome" rowSpan={3}>
                    {minha ? 'Minha escala' : `${String(linhas.length)} pessoas`}
                  </th>
                  {dias.map((d) => (
                    <th key={d} data-dia={d} className={classeDoDia(d, q.hoje, feriados)}>
                      {mesCurto(d)}
                    </th>
                  ))}
                </tr>
                <tr>
                  {dias.map((d) => (
                    <th
                      key={d}
                      data-dia={d}
                      className={classeDoDia(d, q.hoje, feriados)}
                      title={feriados.get(d) ?? undefined}
                    >
                      {siglaDoDia(d)}
                    </th>
                  ))}
                </tr>
                <tr>
                  {dias.map((d) => (
                    <th
                      key={d}
                      data-dia={d}
                      className={`${classeDoDia(d, q.hoje, feriados)} qh-num`}
                      title={feriados.get(d) ?? undefined}
                    >
                      {editor && !minha ? (
                        <button
                          type="button"
                          className="qh-data-botao"
                          onClick={() => setFeriadoAberto(d)}
                          aria-label={`${diaLongo(d)}${feriados.has(d) ? ` — feriado: ${feriados.get(d) ?? ''}` : ''}. Marcar ou tirar feriado`}
                        >
                          {Number(d.slice(8, 10))}
                        </button>
                      ) : (
                        Number(d.slice(8, 10))
                      )}
                    </th>
                  ))}
                </tr>
                {editor && !minha && (
                  <tr className="qh-nao-imprime">
                    <th className="qh-nome qh-copiar-rot">Copiar / colar semana</th>
                    {semanas.map((s) => (
                      <th key={s} colSpan={7} className="qh-copiar">
                        <span className="qh-copiar-acoes">
                          <button
                            type="button"
                            onClick={() => copiarSemana(s)}
                            aria-pressed={copiada?.semana === s}
                            className={copiada?.semana === s ? 'qh-copiar-on' : undefined}
                          >
                            <Icone nome="file" tamanho={13} />
                            {copiada?.semana === s ? 'Copiada' : 'Copiar'} {diaMes(s)}–
                            {diaMes(somarDias(s, 6))}
                          </button>
                          {copiada && copiada.semana !== s && (
                            <button
                              type="button"
                              className="qh-colar"
                              onClick={() => colarSemana(s)}
                            >
                              <Icone nome="down" tamanho={13} />
                              Colar aqui
                            </button>
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                )}
              </thead>
              <tbody>
                {linhas.map((p) => (
                  <tr key={p.id} className={p.id === q.euPessoaId ? 'qh-eu' : undefined}>
                    <th className="qh-nome" scope="row">
                      {editor ? (
                        <button
                          type="button"
                          className="qh-nome-botao"
                          onClick={() => abrirFicha(p)}
                          title={`Abrir a ficha de ${p.nome}`}
                        >
                          {p.nome.toUpperCase()}
                        </button>
                      ) : (
                        p.nome.toUpperCase()
                      )}
                      {!p.ativo && <span className="qh-inativo"> (saiu)</span>}
                    </th>
                    {dias.map((d) => {
                      const c = celulas.get(`${p.id}|${d}`);
                      const t = c ? turnos.get(c.turno) : undefined;
                      const titulo = c
                        ? `${p.nome} · ${diaLongo(d)} · ${t?.rotulo ?? c.turno}${c.setor ? ` · ${c.setor}` : ''}${c.compensa ? ` (feriado de ${diaMes(c.compensa)})` : ''}${c.observacao ? ` · ${c.observacao}` : ''}`
                        : `${p.nome} · ${diaLongo(d)} · vazio`;
                      return (
                        <td
                          key={d}
                          data-dia={d}
                          className={`qh-cel ${classeDoDia(d, q.hoje, feriados)}${editor ? ' qh-cel-edita' : ''}${selecao.has(chave(p.id, d)) ? ' qh-cel-sel' : ''}`}
                          style={c ? estiloDoTurno(t) : undefined}
                          title={titulo}
                          onMouseDown={(e) => {
                            // Shift + clique não pinta o texto da planilha de azul
                            if (e.shiftKey) e.preventDefault();
                          }}
                          onClick={(e) =>
                            clicar(p, d, { shift: e.shiftKey, ctrl: e.ctrlKey || e.metaKey })
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              clicar(p, d, { shift: e.shiftKey, ctrl: e.ctrlKey || e.metaKey });
                            }
                          }}
                          tabIndex={editor ? 0 : undefined}
                          role={editor ? 'button' : undefined}
                          aria-label={editor ? titulo : undefined}
                        >
                          <span className="qh-cel-dentro">
                            <span className="qh-marca">{c ? textoDaCelula(c, t) : ''}</span>
                            {c?.setor && <span className="qh-setor">{c.setor}</span>}
                          </span>
                          {c?.observacao && <span className="qh-obs" aria-hidden="true" />}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <BarraDeRolagem
            alvo={rolagemRef}
            aoRolar={rolar}
            semanas={semanas}
            aoIrPara={irParaSemana}
          />
          <Legenda
            turnos={q.turnos}
            feriados={q.feriados}
            aniversariantes={q.aniversariantes}
            ferias={editor ? blocosDeFerias(ferias, q.pessoas) : null}
            de={de}
            ate={ate}
          />
        </div>
      )}

      {ficha && (
        <FichaDaPessoa
          sessao={sessao}
          pessoa={ficha}
          parte="dados"
          turnos={q.turnos.filter((t) => t.ativo && t.categoria === 'TRABALHO')}
          contas={contas}
          aoFechar={() => setFicha(null)}
          aoMudar={() => carregar()}
        />
      )}

      {aberta?.length === 1 && aberta[0] && (
        <EditorDaCelula
          pessoa={aberta[0].pessoa}
          dia={aberta[0].dia}
          celula={celulas.get(`${aberta[0].pessoa.id}|${aberta[0].dia}`) ?? null}
          turnos={q.turnos}
          feriados={q.feriadosParaCompensar}
          aoFechar={() => setAberta(null)}
          aoGravar={(m, texto) => {
            setAberta(null);
            void gravar(m, texto);
          }}
        />
      )}

      {aberta && aberta.length > 1 && (
        <EditorEmLote
          alvos={aberta}
          turnos={q.turnos}
          aoFechar={() => setAberta(null)}
          aoGravar={(m, texto) => {
            setAberta(null);
            setSelecao(new Set());
            ancora.current = null;
            void gravar(m, texto);
          }}
        />
      )}

      {feriadoAberto && (
        <EditorDoFeriado
          sessao={sessao}
          dia={feriadoAberto}
          nome={feriados.get(feriadoAberto) ?? null}
          aoFechar={() => setFeriadoAberto(null)}
          aoMudar={(texto) => {
            setFeriadoAberto(null);
            setAviso({ tom: 'ok', texto });
            carregar();
          }}
          aoErro={(texto) => setAviso({ tom: 'erro', texto })}
        />
      )}
    </div>
  );
}

function classeDoDia(d: string, hoje: string, feriados: ReadonlyMap<string, string>): string {
  const c: string[] = [];
  if (diaDaSemana(d) === 0) c.push('qh-dom');
  if (feriados.has(d)) c.push('qh-feriado');
  if (d === hoje) c.push('qh-hoje');
  // a linha grossa separa as semanas: a segunda abre a semana (dono 26/10)
  if (diaDaSemana(d) === 1) c.push('qh-seg');
  return c.join(' ');
}

function aplicarLocal(q: QuadroDeHorario, m: readonly MudancaDeCelula[]): QuadroDeHorario {
  const mapa = new Map(q.celulas.map((c) => [`${c.pessoaId}|${c.dia}`, c]));
  for (const x of m) {
    const k = `${x.pessoaId}|${x.dia}`;
    if (x.turno === null) mapa.delete(k);
    else {
      const antes = mapa.get(k);
      mapa.set(k, {
        pessoaId: x.pessoaId,
        dia: x.dia,
        turno: x.turno,
        setor: x.setor !== undefined ? (x.setor ?? null) : (antes?.setor ?? null),
        compensa: x.compensa ?? null,
        observacao:
          x.observacao !== undefined ? (x.observacao ?? null) : (antes?.observacao ?? null),
      });
    }
  }
  return { ...q, celulas: [...mapa.values()] };
}

/* ------------------------------------------------------------------ *
 * Legenda + aniversariantes (o rodapé da planilha)
 * ------------------------------------------------------------------ */

export interface BlocoDeFeriasNoQuadro {
  readonly pessoaId: string;
  readonly nome: string;
  readonly de: string;
  readonly ate: string;
  readonly dias: number;
}

/** Os dias FE de cada pessoa viram blocos corridos ("Ana 03/11 a 17/11, 15 dias"). */
export function blocosDeFerias(
  dias: readonly { pessoaId: string; dia: string }[],
  pessoas: readonly PessoaDaEscala[],
): BlocoDeFeriasNoQuadro[] {
  const nome = new Map(pessoas.map((p) => [p.id, p.nome]));
  const porPessoa = new Map<string, string[]>();
  for (const d of dias) porPessoa.set(d.pessoaId, [...(porPessoa.get(d.pessoaId) ?? []), d.dia]);
  const out: BlocoDeFeriasNoQuadro[] = [];
  for (const [pessoaId, lista] of porPessoa) {
    const ordenados = [...new Set(lista)].sort();
    let de = ordenados[0];
    let ate = de;
    const fechar = () => {
      if (de && ate)
        out.push({
          pessoaId,
          nome: nome.get(pessoaId) ?? '—',
          de,
          ate,
          dias: diasDoPeriodo(de, ate).length,
        });
    };
    for (const d of ordenados.slice(1)) {
      if (ate && d === somarDias(ate, 1)) ate = d;
      else {
        fechar();
        de = d;
        ate = d;
      }
    }
    fechar();
  }
  return out.sort((a, b) => a.de.localeCompare(b.de) || a.nome.localeCompare(b.nome));
}

const fimDoMes = (dia: string) =>
  somarDias(`${somarDias(`${dia.slice(0, 8)}28`, 4).slice(0, 8)}01`, -1);

function Legenda({
  turnos,
  feriados,
  aniversariantes,
  ferias,
  de,
  ate,
}: {
  turnos: readonly TurnoDaEscala[];
  feriados: QuadroDeHorario['feriados'];
  aniversariantes: QuadroDeHorario['aniversariantes'];
  /** null = quem não edita (não lê as férias de todos). */
  ferias: readonly BlocoDeFeriasNoQuadro[] | null;
  de: string;
  ate: string;
}) {
  // "férias do mês": o(s) mês(es) que o quadro mostra; "próximas": as que começam depois dele
  const inicioMes = `${de.slice(0, 8)}01`;
  const fimMes = fimDoMes(ate);
  const doMes = (ferias ?? []).filter((f) => f.ate >= inicioMes && f.de <= fimMes);
  const proximas = (ferias ?? []).filter((f) => f.de > fimMes).slice(0, 8);
  const mesesDoQuadro =
    de.slice(0, 7) === ate.slice(0, 7) ? nomeDoMes(de) : `${nomeDoMes(de)} e ${nomeDoMes(ate)}`;
  return (
    <div className={`qh-rodape${ferias ? ' qh-rodape-com-ferias' : ''}`}>
      <div className="qh-legenda">
        <h2>Legenda</h2>
        <ul>
          {turnos
            .filter((t) => t.ativo)
            .map((t) => (
              <li key={t.codigo}>
                <span className="qh-amostra" style={estiloDoTurno(t)}>
                  {t.categoria === 'COMPENSADA' ? 'F12' : t.marca || ' '}
                </span>
                <span>
                  {t.rotulo}
                  {horarioDoTurno(t) && <small> · {horarioDoTurno(t)}</small>}
                  {t.categoria === 'COMPENSADA' && (
                    <small> · F12 = folga referente ao dia 12</small>
                  )}
                </span>
              </li>
            ))}
          <li>
            <span className="qh-amostra qh-amostra-dom">&nbsp;</span>
            <span>Domingo</span>
          </li>
        </ul>
      </div>
      {feriados.length > 0 && (
        <div className="qh-legenda">
          <h2>Feriados</h2>
          <ul>
            {feriados.map((f) => (
              <li key={f.dia}>
                <span className="qh-amostra qh-amostra-feriado">{Number(f.dia.slice(8, 10))}</span>
                <span>
                  {diaMes(f.dia)} · {f.nome}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {ferias && (
        <div className="qh-legenda qh-legenda-ferias">
          <h2>Férias em {mesesDoQuadro}</h2>
          {doMes.length === 0 ? (
            <p className="qh-mudo">Ninguém de férias nestes meses.</p>
          ) : (
            <ul>
              {doMes.map((f) => (
                <li key={`${f.pessoaId}${f.de}`}>
                  <span className="qh-amostra qh-amostra-fe">FE</span>
                  <span>
                    <strong>{f.nome}</strong> · {diaMes(f.de)} a {diaMes(f.ate)}{' '}
                    <small>({f.dias} dias)</small>
                  </span>
                </li>
              ))}
            </ul>
          )}
          <h2 className="qh-legenda-sub">Próximas férias</h2>
          {proximas.length === 0 ? (
            <p className="qh-mudo">Nenhuma marcada depois deste mês.</p>
          ) : (
            <ul>
              {proximas.map((f) => (
                <li key={`${f.pessoaId}${f.de}`}>
                  <span className="qh-amostra qh-amostra-fe">FE</span>
                  <span>
                    <strong>{f.nome}</strong> · {diaMes(f.de)} a {diaMes(f.ate)}{' '}
                    <small>({f.dias} dias)</small>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="qh-legenda">
        <h2>Aniversariantes</h2>
        {aniversariantes.length === 0 ? (
          <p className="qh-mudo">
            Ninguém do quadro faz aniversário nestes meses (ou o dia não está no cadastro).
          </p>
        ) : (
          <ul>
            {aniversariantes.map((a) => (
              <li key={`${a.nome}${String(a.mes)}${String(a.dia)}`}>
                <span className="qh-amostra qh-amostra-anv">🎂</span>
                <span>
                  {String(a.dia).padStart(2, '0')}/{String(a.mes).padStart(2, '0')} · {a.nome}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Minha escala (celular)
 * ------------------------------------------------------------------ */

function MinhaEscala({
  q,
  eu,
  turnos,
  celulas,
  feriados,
}: {
  q: QuadroDeHorario;
  eu: PessoaDaEscala;
  turnos: ReadonlyMap<string, TurnoDaEscala>;
  celulas: ReadonlyMap<string, CelulaDaEscala>;
  feriados: ReadonlyMap<string, string>;
}) {
  const [controle, setControle] = useState<ControleDaPessoa | null>(null);
  const [pendentes, setPendentes] = useState<PedidoDaEscala[]>([]);
  const [ferias, setFerias] = useState<FeriasDaPessoaNaEscala | null>(null);
  useEffect(() => {
    void lerEu().then((r) => {
      if (!r.ok) return;
      setControle(r.dados.controle);
      setFerias(r.dados.ferias ?? null);
      setPendentes(r.dados.pedidos.filter((p) => p.estado === 'PENDENTE'));
    });
  }, []);
  const dias = diasDoPeriodo(q.de, q.ate).filter((d) => d >= q.hoje);
  return (
    <div className="qh-minha qh-nao-imprime">
      {controle && (
        <div className="qh-kpis">
          <Kpi
            rotulo="Feriados a compensar"
            valor={controle.feriadosAPagar.length}
            destaque={controle.feriadosAPagar.length > 0}
          />
          <Kpi rotulo="Faltas no ano" valor={controle.faltas} />
          <Kpi rotulo="Atestados no ano" valor={controle.atestados} />
          <Kpi
            rotulo="Próximas férias"
            valor={
              controle.proximasFerias
                ? `${diaMes(controle.proximasFerias.de)}–${diaMes(controle.proximasFerias.ate)}`
                : '—'
            }
          />
        </div>
      )}
      {pendentes.length > 0 && (
        <p className="qh-aviso qh-aviso-info">
          {pendentes.length === 1
            ? '1 pedido seu aguardando'
            : `${String(pendentes.length)} pedidos seus aguardando`}{' '}
          o Carlos.
          <a href="#/escala?aba=pedidos"> Ver pedidos</a>
        </p>
      )}
      {ferias && <MinhasFerias f={ferias} />}
      <ol className="qh-dias">
        {dias.map((d) => {
          const c = celulas.get(`${eu.id}|${d}`);
          const t = c ? turnos.get(c.turno) : undefined;
          // o horário da pessoa (ficha) vale mais que o geral do turno nos dias de trabalho
          const meu = eu.horario;
          const h =
            t?.categoria === 'TRABALHO' && (meu.entrada || meu.saida)
              ? `${meu.entrada ?? '?'}–${meu.saida ?? '?'}${meu.almocoInicio && meu.almocoFim ? ` · almoço ${meu.almocoInicio}–${meu.almocoFim}` : ''}`
              : t
                ? horarioDoTurno(t)
                : null;
          return (
            <li key={d} className={d === q.hoje ? 'qh-dia qh-dia-hoje' : 'qh-dia'}>
              <span className="qh-dia-data">
                <strong>{d === q.hoje ? 'Hoje' : siglaDoDia(d)}</strong>
                {diaMes(d)}
              </span>
              <span className="qh-amostra qh-amostra-grande" style={estiloDoTurno(t)}>
                {c ? textoDaCelula(c, t) || ' ' : '—'}
              </span>
              <span className="qh-dia-txt">
                <strong>{t?.rotulo ?? 'Sem escala'}</strong>
                {h && <small>{h}</small>}
                {c?.setor && <small>Setor: {c.setor}</small>}
                {t?.categoria === 'TRABALHO' && eu.horario.abreLoja && <small>Abre a loja</small>}
                {t?.categoria === 'TRABALHO' && eu.horario.fechaLoja && <small>Fecha a loja</small>}
                {c?.compensa && <small>Folga do feriado de {diaMes(c.compensa)}</small>}
                {feriados.get(d) && (
                  <small className="qh-tag-feriado">Feriado: {feriados.get(d)}</small>
                )}
                {c?.observacao && <small>{c.observacao}</small>}
              </span>
            </li>
          );
        })}
      </ol>
      <a className="qh-btn qh-btn-pri" href="#/escala?aba=pedidos&novo=1">
        <Icone nome="plus" tamanho={15} />
        Pedir folga, troca, férias ou mandar atestado
      </a>
    </div>
  );
}

export function Kpi({
  rotulo,
  valor,
  destaque,
}: {
  rotulo: string;
  valor: string | number;
  destaque?: boolean;
}) {
  return (
    <div className={`qh-kpi${destaque ? ' qh-kpi-destaque' : ''}`}>
      <span>{rotulo}</span>
      <strong>{valor}</strong>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Editor da célula (Carlos/Nicolas)
 * ------------------------------------------------------------------ */

function EditorDaCelula({
  pessoa,
  dia,
  celula,
  turnos,
  feriados,
  aoFechar,
  aoGravar,
}: {
  pessoa: PessoaDaEscala;
  dia: string;
  celula: CelulaDaEscala | null;
  turnos: readonly TurnoDaEscala[];
  feriados: QuadroDeHorario['feriados'];
  aoFechar: () => void;
  aoGravar: (m: MudancaDeCelula[], texto: string) => void;
}) {
  const [turno, setTurno] = useState<string | null>(celula?.turno ?? null);
  const [setor, setSetor] = useState(celula?.setor ?? '');
  const [compensa, setCompensa] = useState(celula?.compensa ?? '');
  const [obs, setObs] = useState(celula?.observacao ?? '');
  const [ateDia, setAteDia] = useState(dia);
  // "pôs abertura pra Maria num domingo: já passa pra todo domingo, a não ser que o Carlos troque"
  const [repetir, setRepetir] = useState(false);
  const [repetirAte, setRepetirAte] = useState(`${dia.slice(0, 4)}-12-31`);
  const [salvando, setSalvando] = useState(false);
  const t = turnos.find((x) => x.codigo === turno);
  const variosDias =
    t && (t.categoria === 'FERIAS' || t.categoria === 'ATESTADO' || t.categoria === 'FALTA');

  useEscFecha(aoFechar);

  const salvar = async () => {
    const ate = variosDias && ateDia > dia ? ateDia : dia;
    const m: MudancaDeCelula[] = diasDoPeriodo(dia, ate).map((d) => ({
      pessoaId: pessoa.id,
      dia: d,
      turno,
      setor: setor.trim() || null,
      compensa: t?.categoria === 'COMPENSADA' ? compensa || null : null,
      observacao: obs.trim() || null,
    }));
    if (repetir && !variosDias && repetirAte > dia) {
      // o mesmo dia da semana até a data escolhida; nunca por cima de férias, falta, atestado,
      // folga compensada ou feriado (esses ficam como estão)
      setSalvando(true);
      const protegidos = new Set<string>();
      const categoria = new Map(turnos.map((x) => [x.codigo, x.categoria]));
      for (let de = somarDias(dia, 7); de <= repetirAte; de = somarDias(de, 93)) {
        const ateBloco = somarDias(de, 92) < repetirAte ? somarDias(de, 92) : repetirAte;
        const r = await lerQuadro(de, ateBloco);
        if (!r.ok) {
          setSalvando(false);
          return;
        }
        for (const c of r.dados.celulas) {
          const cat = categoria.get(c.turno);
          if (c.pessoaId === pessoa.id && cat !== 'TRABALHO' && cat !== 'FOLGA')
            protegidos.add(c.dia);
        }
      }
      let pulados = 0;
      for (let d = somarDias(dia, 7); d <= repetirAte; d = somarDias(d, 7)) {
        if (protegidos.has(d)) {
          pulados += 1;
          continue;
        }
        m.push({ ...m[0], dia: d } as MudancaDeCelula);
      }
      aoGravar(
        m,
        `${pessoa.nome}: ${t?.rotulo ?? 'apagado'} ${[0, 6].includes(diaDaSemana(dia)) ? 'todo' : 'toda'} ${nomeDoDia(dia)} de ${diaMes(dia)} até ${diaMes(repetirAte)} (${String(m.length)} dias${pulados ? `; ${String(pulados)} com férias/falta/feriado ficaram como estavam` : ''}).`,
      );
      return;
    }
    aoGravar(
      m,
      `${pessoa.nome}: ${t?.rotulo ?? 'apagado'}${m.length > 1 ? ` de ${diaMes(dia)} a ${diaMes(ate)}` : ` em ${diaMes(dia)}`}.`,
    );
  };

  return (
    <div className="qh-modal-fundo" onClick={aoFechar} role="presentation">
      <div
        className="qh-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`${pessoa.nome}, ${diaLongo(dia)}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <div>
            <h2>{pessoa.nome}</h2>
            <p>{diaLongo(dia)}</p>
          </div>
          <button type="button" className="qh-btn-icone" onClick={aoFechar} aria-label="Fechar">
            <Icone nome="x" tamanho={16} />
          </button>
        </header>
        <div className="qh-opcoes">
          {turnos
            .filter((x) => x.ativo)
            .map((x) => (
              <button
                key={x.codigo}
                type="button"
                className={`qh-opcao${turno === x.codigo ? ' qh-opcao-on' : ''}`}
                onClick={() => setTurno(x.codigo)}
                aria-pressed={turno === x.codigo}
              >
                <span className="qh-amostra" style={estiloDoTurno(x)}>
                  {x.categoria === 'COMPENSADA' ? 'FC' : x.marca || ' '}
                </span>
                <span>
                  {x.rotulo}
                  {horarioDoTurno(x) && <small>{horarioDoTurno(x)}</small>}
                </span>
              </button>
            ))}
          <button
            type="button"
            className={`qh-opcao${turno === null ? ' qh-opcao-on' : ''}`}
            onClick={() => setTurno(null)}
            aria-pressed={turno === null}
          >
            <span className="qh-amostra qh-amostra-borracha">
              <Icone nome="x" tamanho={12} />
            </span>
            <span>Vazio</span>
          </button>
        </div>
        {t?.categoria === 'COMPENSADA' && (
          <label className="qh-campo">
            Compensa qual feriado?
            <select value={compensa} onChange={(e) => setCompensa(e.target.value)}>
              <option value="">Não apontar</option>
              {feriadosPerto(feriados, dia).map((f) => (
                <option key={f.dia} value={f.dia}>
                  {diaMes(f.dia)} · {f.nome}
                </option>
              ))}
            </select>
          </label>
        )}
        {variosDias && (
          <label className="qh-campo">
            Até que dia? (marca o período todo)
            <input
              type="date"
              value={ateDia}
              min={dia}
              onChange={(e) => setAteDia(e.target.value || dia)}
            />
          </label>
        )}
        {turno !== null && t?.categoria === 'TRABALHO' && (
          <label className="qh-campo">
            Setor do dia (opcional)
            <input
              list="qh-setores"
              value={setor}
              maxLength={30}
              placeholder="caixa, balcão, entrega, farmácia…"
              onChange={(e) => setSetor(e.target.value)}
            />
            <datalist id="qh-setores">
              {SETORES.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </label>
        )}
        {turno !== null && (
          <label className="qh-campo">
            Observação (opcional)
            <input value={obs} maxLength={200} onChange={(e) => setObs(e.target.value)} />
          </label>
        )}
        {!variosDias && (
          <div className="qh-repetir">
            <label className="qh-check">
              <input
                type="checkbox"
                checked={repetir}
                onChange={(e) => setRepetir(e.target.checked)}
              />
              <span>
                <strong>
                  Repetir {[0, 6].includes(diaDaSemana(dia)) ? 'todo' : 'toda'} {nomeDoDia(dia)}{' '}
                  daqui pra frente
                </strong>
                <small>
                  Férias, faltas, atestados, folgas de feriado e feriados ficam como estão; depois
                  dá para trocar um dia só.
                </small>
              </span>
            </label>
            {repetir && (
              <label className="qh-campo qh-repetir-ate">
                Até
                <input
                  type="date"
                  value={repetirAte}
                  min={somarDias(dia, 7)}
                  onChange={(e) => e.target.value && setRepetirAte(e.target.value)}
                />
              </label>
            )}
          </div>
        )}
        <footer>
          <button type="button" className="qh-btn qh-btn-sec" onClick={aoFechar}>
            Cancelar
          </button>
          <button
            type="button"
            className="qh-btn qh-btn-pri"
            onClick={() => void salvar()}
            disabled={salvando}
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </button>
        </footer>
      </div>
    </div>
  );
}

/** Feriados de até 120 dias antes da folga (o "12" amarelo paga um feriado que já passou). */
function feriadosPerto(feriados: QuadroDeHorario['feriados'], dia: string) {
  return feriados
    .filter((f) => f.dia <= dia && f.dia >= somarDias(dia, -120))
    .sort((a, b) => b.dia.localeCompare(a.dia));
}

const NOMES_DOS_MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];
const nomeDoMes = (dia: string) => NOMES_DOS_MESES[Number(dia.slice(5, 7)) - 1] ?? '';

/* ------------------------------------------------------------------ *
 * A barra do rodapé (dono 26/10: "avançar e voltar entre os dias sem precisar ir até outra
 * parte da tela") — fica grudada no pé da tela e anda junto com a planilha.
 * ------------------------------------------------------------------ */

function BarraDeRolagem({
  alvo,
  aoRolar,
  semanas,
  aoIrPara,
}: {
  alvo: React.RefObject<HTMLDivElement | null>;
  aoRolar: (dias: number) => void;
  semanas: readonly string[];
  aoIrPara: (semana: string) => void;
}) {
  const [pos, setPos] = useState({ x: 0, max: 0 });
  useEffect(() => {
    const el = alvo.current;
    if (!el) return;
    const medir = () => setPos({ x: el.scrollLeft, max: el.scrollWidth - el.clientWidth });
    medir();
    el.addEventListener('scroll', medir, { passive: true });
    window.addEventListener('resize', medir);
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(medir);
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', medir);
      window.removeEventListener('resize', medir);
      ro?.disconnect();
    };
  }, [alvo]);
  if (pos.max <= 1) return null;
  return (
    <div className="qh-rolagem-rodape qh-nao-imprime" role="group" aria-label="Andar pelos dias">
      <button
        type="button"
        className="qh-btn-icone"
        onClick={() => aoRolar(-7)}
        title="Voltar 7 dias"
      >
        <Icone nome="arrow-l" tamanho={15} />
        <span className="qh-rr-txt">7</span>
      </button>
      <button
        type="button"
        className="qh-btn-icone"
        onClick={() => aoRolar(-1)}
        aria-label="Voltar 1 dia"
      >
        <Icone nome="arrow-l" tamanho={13} />
      </button>
      <input
        type="range"
        className="qh-rr-barra"
        min={0}
        max={Math.round(pos.max)}
        value={Math.round(pos.x)}
        aria-label="Rolar a planilha"
        onChange={(e) => {
          const el = alvo.current;
          if (el) el.scrollLeft = Number(e.target.value);
        }}
      />
      <button
        type="button"
        className="qh-btn-icone"
        onClick={() => aoRolar(1)}
        aria-label="Avançar 1 dia"
      >
        <Icone nome="arrow" tamanho={13} />
      </button>
      <button
        type="button"
        className="qh-btn-icone"
        onClick={() => aoRolar(7)}
        title="Avançar 7 dias"
      >
        <span className="qh-rr-txt">7</span>
        <Icone nome="arrow" tamanho={15} />
      </button>
      <span className="qh-rr-semanas">
        {semanas.map((s) => (
          <button key={s} type="button" className="qh-rr-semana" onClick={() => aoIrPara(s)}>
            {diaMes(s)}
          </button>
        ))}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Várias células de uma vez (Shift + clique)
 * ------------------------------------------------------------------ */

function EditorEmLote({
  alvos,
  turnos,
  aoFechar,
  aoGravar,
}: {
  alvos: readonly { pessoa: PessoaDaEscala; dia: string }[];
  turnos: readonly TurnoDaEscala[];
  aoFechar: () => void;
  aoGravar: (m: MudancaDeCelula[], texto: string) => void;
}) {
  const [turno, setTurno] = useState<string | null | undefined>(undefined);
  const [mexerSetor, setMexerSetor] = useState(false);
  const [setor, setSetor] = useState('');
  const t = turnos.find((x) => x.codigo === turno);
  const pessoas = new Set(alvos.map((a) => a.pessoa.id)).size;
  const dias = [...new Set(alvos.map((a) => a.dia))].sort();

  useEscFecha(aoFechar);

  const salvar = () => {
    if (turno === undefined) return;
    const m: MudancaDeCelula[] = alvos.map((a) => ({
      pessoaId: a.pessoa.id,
      dia: a.dia,
      turno,
      ...(mexerSetor && t?.categoria === 'TRABALHO' ? { setor: setor.trim() || null } : {}),
      ...(t?.categoria === 'COMPENSADA' ? { compensa: null } : {}),
    }));
    aoGravar(
      m,
      `${String(m.length)} células (${String(pessoas)} pessoa${pessoas === 1 ? '' : 's'}): ${t?.rotulo ?? 'apagadas'}.`,
    );
  };

  return (
    <div className="qh-modal-fundo" onClick={aoFechar} role="presentation">
      <div
        className="qh-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Alterar ${String(alvos.length)} células`}
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <div>
            <h2>Alterar {alvos.length} células</h2>
            <p>
              {pessoas} pessoa{pessoas === 1 ? '' : 's'} ·{' '}
              {dias.length === 1
                ? diaLongo(dias[0] ?? '')
                : `${diaMes(dias[0] ?? '')} a ${diaMes(dias[dias.length - 1] ?? '')}`}
            </p>
          </div>
          <button type="button" className="qh-btn-icone" onClick={aoFechar} aria-label="Fechar">
            <Icone nome="x" tamanho={16} />
          </button>
        </header>
        <div className="qh-opcoes">
          {turnos
            .filter((x) => x.ativo)
            .map((x) => (
              <button
                key={x.codigo}
                type="button"
                className={`qh-opcao${turno === x.codigo ? ' qh-opcao-on' : ''}`}
                onClick={() => setTurno(x.codigo)}
                aria-pressed={turno === x.codigo}
              >
                <span className="qh-amostra" style={estiloDoTurno(x)}>
                  {x.categoria === 'COMPENSADA' ? 'FC' : x.marca || ' '}
                </span>
                <span>
                  {x.rotulo}
                  {horarioDoTurno(x) && <small>{horarioDoTurno(x)}</small>}
                </span>
              </button>
            ))}
          <button
            type="button"
            className={`qh-opcao${turno === null ? ' qh-opcao-on' : ''}`}
            onClick={() => setTurno(null)}
            aria-pressed={turno === null}
          >
            <span className="qh-amostra qh-amostra-borracha">
              <Icone nome="x" tamanho={12} />
            </span>
            <span>Apagar (vazio)</span>
          </button>
        </div>
        {t?.categoria === 'TRABALHO' && (
          <div className="qh-repetir">
            <label className="qh-check">
              <input
                type="checkbox"
                checked={mexerSetor}
                onChange={(e) => setMexerSetor(e.target.checked)}
              />
              <span>
                <strong>Trocar também o setor</strong>
                <small>Desmarcado = cada célula fica com o setor que já tinha.</small>
              </span>
            </label>
            {mexerSetor && (
              <label className="qh-campo">
                Setor (vazio = sem setor)
                <input
                  list="qh-setores-lote"
                  value={setor}
                  maxLength={30}
                  placeholder="Caixa 1, balcão, entrega…"
                  onChange={(e) => setSetor(e.target.value)}
                />
                <datalist id="qh-setores-lote">
                  {SETORES.map((x) => (
                    <option key={x} value={x} />
                  ))}
                </datalist>
              </label>
            )}
          </div>
        )}
        <p className="qh-mudo">
          Vale para todas as células selecionadas, inclusive as que têm férias, falta ou atestado.
        </p>
        <footer>
          <button type="button" className="qh-btn qh-btn-sec" onClick={aoFechar}>
            Cancelar
          </button>
          <button
            type="button"
            className="qh-btn qh-btn-pri"
            onClick={salvar}
            disabled={turno === undefined}
          >
            Aplicar em {alvos.length}
          </button>
        </footer>
      </div>
    </div>
  );
}

export const SETORES = [
  'Caixa 1',
  'Caixa 2',
  'Caixa 3',
  'Balcão',
  'Entrega',
  'Farmácia',
  'Perfumaria',
  'Estoque',
  'Gerência',
];

/* ------------------------------------------------------------------ *
 * Feriado (clicar na data — dono 26/10: "marcar feriado municipal")
 * ------------------------------------------------------------------ */

const TIPOS_DE_FERIADO = [
  'Feriado municipal',
  'Feriado estadual',
  'Feriado nacional',
  'Ponto facultativo',
];

function EditorDoFeriado({
  sessao,
  dia,
  nome,
  aoFechar,
  aoMudar,
  aoErro,
}: {
  sessao: Sessao;
  dia: string;
  nome: string | null;
  aoFechar: () => void;
  aoMudar: (texto: string) => void;
  aoErro: (texto: string) => void;
}) {
  const [texto, setTexto] = useState(nome ?? 'Feriado municipal');
  const [salvando, setSalvando] = useState(false);
  useEscFecha(aoFechar);
  const fr = (n: number | undefined, pos: boolean) =>
    n
      ? ` ${String(n)} pessoa${n === 1 ? '' : 's'} que não trabalha${n === 1 ? '' : 'm'} feriado ${pos ? 'ficou com FR' : 'voltou ao turno de sempre'}.`
      : '';

  const marcar = async () => {
    if (texto.trim().length < 2) return;
    setSalvando(true);
    const r = await incluirFeriado(sessao, { dia, nome: texto.trim() });
    setSalvando(false);
    if (r.tipo === 'confirmada')
      aoMudar(`${diaLongo(dia)}: ${texto.trim()}.${fr(r.dados.ajustadas, true)}`);
    else aoErro(r.tipo === 'recusada' ? r.mensagem : 'A internet caiu — confira o quadro.');
  };
  const tirar = async () => {
    setSalvando(true);
    const r = await removerFeriado(sessao, dia);
    setSalvando(false);
    if (r.tipo === 'confirmada')
      aoMudar(`${diaLongo(dia)} não é mais feriado.${fr(r.dados.ajustadas, false)}`);
    else aoErro(r.tipo === 'recusada' ? r.mensagem : 'A internet caiu — confira o quadro.');
  };

  return (
    <div className="qh-modal-fundo" onClick={aoFechar} role="presentation">
      <div
        className="qh-modal qh-modal-estreito"
        role="dialog"
        aria-modal="true"
        aria-label={`Feriado em ${diaLongo(dia)}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <div>
            <h2>{nome ? 'Feriado' : 'Marcar feriado'}</h2>
            <p>{diaLongo(dia)}</p>
          </div>
          <button type="button" className="qh-btn-icone" onClick={aoFechar} aria-label="Fechar">
            <Icone nome="x" tamanho={16} />
          </button>
        </header>
        <div className="qh-feriado-tipos">
          {TIPOS_DE_FERIADO.map((x) => (
            <button
              key={x}
              type="button"
              className={`qh-pincel${texto === x ? ' qh-pincel-on' : ''}`}
              onClick={() => setTexto(x)}
              aria-pressed={texto === x}
            >
              {x}
            </button>
          ))}
        </div>
        <label className="qh-campo">
          Nome do feriado
          <input
            value={texto}
            maxLength={60}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="ex.: Aniversário de Muriaé"
          />
        </label>
        <p className="qh-mudo">
          Quem tem “não trabalha feriado” na ficha fica com FR nesse dia, na hora. Férias, faltas,
          atestados, folgas e pedidos aprovados ficam como estão.
        </p>
        <footer>
          {nome && (
            <button
              type="button"
              className="qh-btn qh-btn-sec qh-btn-perigo"
              onClick={() => void tirar()}
              disabled={salvando}
            >
              Tirar feriado
            </button>
          )}
          <button type="button" className="qh-btn qh-btn-sec" onClick={aoFechar}>
            Cancelar
          </button>
          <button
            type="button"
            className="qh-btn qh-btn-pri"
            onClick={() => void marcar()}
            disabled={salvando || texto.trim().length < 2}
          >
            {salvando ? 'Salvando…' : nome ? 'Salvar nome' : 'Marcar feriado'}
          </button>
        </footer>
      </div>
    </div>
  );
}
